import React, { useState } from 'react';

const WorkshopConference = () => {
  const [activeTab, setActiveTab] = useState('conference');
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Personal Information
    name: '',
    mobile: '',
    email: '',
    gender: '',
    dateOfBirth: '',
    fatherName: '',
    motherName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    
    // Educational Information
    college: '',
    university: '',
    department: '',
    educationalBackground: '',
    degree: '',
    specialization: '',
    passingYear: '',
    currentSemester: '',
    cgpa: '',
    percentage: '',
    
    // Professional Information
    workingProfessional: '',
    companyName: '',
    designation: '',
    workExperience: '',
    currentSalary: '',
    
    // Career Information
    careerGap: '',
    gapReason: '',
    gapDuration: '',
    careerGoals: '',
    expectedSalary: '',
    preferredLocation: '',
    
    // Program Information
    interest: [],
    preferredMode: '',
    technicalSkills: '',
    programmingLanguages: '',
    previousWorkshops: '',
    
    // Additional Information
    hobbies: '',
    achievements: '',
    references: '',
    hearAboutUs: '',
    additionalInfo: '',
    
    // Agreement
    agreeToTerms: false,
    agreeToWhatsApp: false
  });

  const conferences = [
    {
      title: "AI in Energy Sector Conference",
      location: "Central University of Karnataka, India",
      date: "March 2025",
      status: "Completed",
      description: "Successfully conducted international conference on AI applications in renewable energy sector with participation from global universities.",
      topics: ["AI Models", "Energy Optimization", "Renewable Energy", "Machine Learning"],
      certificate: "International Conference Certificate",
      eligibility: "Final year students and working professionals"
    },
    {
      title: "Advanced AI & Quantum Computing Summit",
      location: "Mexico",
      date: "November 2025",
      status: "Upcoming",
      description: "International conference focusing on quantum computing applications in AI and advanced machine learning models for energy sector.",
      topics: ["Quantum Computing", "Advanced AI", "LLM Models", "Energy Applications"],
      certificate: "International Conference Certificate",
      eligibility: "Students from reputed institutions with strong academic background"
    },
    {
      title: "Global AI Innovation Conference",
      location: "Italy",
      date: "2026",
      status: "Upcoming",
      description: "Premier international conference on AI innovation, featuring real-time company projects and cutting-edge research presentations.",
      topics: ["AI Innovation", "Industry 4.0", "Smart Energy", "Research Collaboration"],
      certificate: "International Conference Certificate",
      eligibility: "Top performing students with research background"
    },
    {
      title: "Future of Energy & AI Conference",
      location: "International Venue (TBA)",
      date: "2026",
      status: "Planning",
      description: "Comprehensive conference covering the future landscape of AI in energy sector with focus on practical implementations.",
      topics: ["Future Technologies", "AI Ethics", "Sustainable Energy", "Global Collaboration"],
      certificate: "International Conference Certificate",
      eligibility: "Selected candidates based on academic excellence"
    }
  ];

  const workshops = [
    {
      title: "Hybrid AI Workshop Series",
      location: "IITs & Universities across India",
      mode: "Hybrid (On-campus + Virtual)",
      description: "Intensive workshops conducted at premier institutions focusing on real-time company projects and practical AI implementations.",
      features: ["Real-time Projects", "Industry Mentorship", "Hands-on Training", "Networking"],
      certificate: "Workshop Completion Certificate",
      eligibility: "Engineering students from recognized universities"
    },
    {
      title: "Virtual International Workshop",
      location: "Global (Virtual)",
      mode: "Virtual",
      description: "International virtual workshops with technology companies providing real-world project experience and global collaboration opportunities.",
      features: ["Company Projects", "Global Collaboration", "Virtual Labs", "Expert Sessions"],
      certificate: "International Workshop Certificate",
      eligibility: "Students with basic programming knowledge"
    },
    {
      title: "Industry Collaboration Workshop",
      location: "Partner Universities",
      mode: "Hybrid",
      description: "Collaborative workshops with leading technology companies offering direct exposure to industry challenges and solutions.",
      features: ["Industry Partners", "Real Applications", "Project Presentations", "Job Assistance"],
      certificate: "Industry Collaboration Certificate",
      eligibility: "Final year students and recent graduates"
    },
    {
      title: "Research & Development Workshop",
      location: "Research Centers",
      mode: "On-campus",
      description: "Advanced workshops focusing on R&D projects in AI, quantum computing, and energy sector applications.",
      features: ["Research Projects", "Publication Support", "Advanced Topics", "Career Guidance"],
      certificate: "R&D Workshop Certificate",
      eligibility: "Students with research publications or projects"
    }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name === 'interest') {
      setFormData(prev => ({
        ...prev,
        interest: checked 
          ? [...prev.interest, value]
          : prev.interest.filter(item => item !== value)
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    const requiredFields = ['name', 'mobile', 'email', 'gender', 'college', 'department', 'educationalBackground', 'passingYear'];
    
    for (let field of requiredFields) {
      if (!formData[field]) {
        alert(`Please fill in the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field.`);
        return false;
      }
    }
    
    if (formData.interest.length === 0) {
      alert('Please select at least one program interest.');
      return false;
    }
    
    if (!formData.agreeToTerms) {
      alert('Please agree to the terms and conditions.');
      return false;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('Please enter a valid email address.');
      return false;
    }
    
    // Mobile validation
    const mobileRegex = /^[+]?[0-9]{10,15}$/;
    if (!mobileRegex.test(formData.mobile.replace(/\s/g, ''))) {
      alert('Please enter a valid mobile number.');
      return false;
    }
    
    return true;
  };

  const submitToFirebase = async (studentData) => {
    try {
      console.log('Submitting data:', studentData);
      
      // Generate unique ID
      const applicationId = 'WC_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      
      // Simulate Firebase submission (replace with actual Firebase implementation)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      return { success: true, id: applicationId };
      
    } catch (error) {
      console.error('Error submitting:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const studentData = {
        ...formData,
        submissionDate: new Date().toISOString(),
        status: 'pending',
        eligibilityStatus: 'under_review'
      };
      
      const result = await submitToFirebase(studentData);
      
      if (result.success) {
        alert(`🎉 Thank you for your enrollment! Your application ID is: ${result.id}\n\nWe will review your application and contact you within 24-48 hours with further details about your eligibility for our international programs.`);
        
        setShowEnrollModal(false);
        
        // Reset form
        setFormData({
          name: '', mobile: '', email: '', gender: '', dateOfBirth: '', fatherName: '', motherName: '',
          address: '', city: '', state: '', pincode: '', college: '', university: '', department: '',
          educationalBackground: '', degree: '', specialization: '', passingYear: '', currentSemester: '',
          cgpa: '', percentage: '', workingProfessional: '', companyName: '', designation: '',
          workExperience: '', currentSalary: '', careerGap: '', gapReason: '', gapDuration: '',
          careerGoals: '', expectedSalary: '', preferredLocation: '', interest: [], preferredMode: '',
          technicalSkills: '', programmingLanguages: '', previousWorkshops: '', hobbies: '',
          achievements: '', references: '', hearAboutUs: '', additionalInfo: '', agreeToTerms: false,
          agreeToWhatsApp: false
        });
      }
    } catch (error) {
      alert('❌ There was an error submitting your application. Please try again or contact support.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Completed': '#28a745',
      'Upcoming': '#007bff',
      'Planning': '#ffc107'
    };
    return (
      <span 
        style={{
          backgroundColor: colors[status],
          color: 'white',
          padding: '4px 8px',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 'bold'
        }}
      >
        {status}
      </span>
    );
  };

  const styles = {
    container: {
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh',
      padding: '2rem 0'
    },
    innerContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 20px'
    },
    header: {
      textAlign: 'center',
      marginBottom: '3rem'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: '#007bff',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '1.2rem',
      color: '#6c757d',
      marginBottom: '2rem'
    },
    highlightBox: {
      backgroundColor: '#e9ecef',
      padding: '2rem',
      borderRadius: '10px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
    },
    eligibilityAlert: {
      backgroundColor: '#fff3cd',
      border: '1px solid #ffeaa7',
      borderRadius: '8px',
      padding: '1rem',
      marginBottom: '1rem',
      color: '#856404'
    },
    tabContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: '1rem',
      marginBottom: '2rem'
    },
    tab: {
      padding: '1rem 2rem',
      borderRadius: '25px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textDecoration: 'none',
      border: '2px solid #007bff'
    },
    activeTab: {
      backgroundColor: '#007bff',
      color: 'white'
    },
    inactiveTab: {
      backgroundColor: 'white',
      color: '#007bff'
    },
    sectionHeader: {
      backgroundColor: '#007bff',
      color: 'white',
      padding: '2rem',
      borderRadius: '10px',
      marginBottom: '2rem',
      boxShadow: '0 4px 15px rgba(0,123,255,0.3)'
    },
    workshopHeader: {
      backgroundColor: '#28a745'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '15px',
      boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      cursor: 'pointer'
    },
    cardHeader: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '1.5rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    workshopCardHeader: {
      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    },
    cardBody: {
      padding: '1.5rem'
    },
    cardTitle: {
      fontSize: '1.3rem',
      fontWeight: 'bold',
      margin: 0
    },
    locationDate: {
      color: '#6c757d',
      marginBottom: '1rem'
    },
    description: {
      color: '#495057',
      lineHeight: '1.6',
      marginBottom: '1rem'
    },
    topicsContainer: {
      marginBottom: '1rem'
    },
    topicBadge: {
      backgroundColor: '#e9ecef',
      color: '#495057',
      padding: '4px 8px',
      borderRadius: '8px',
      fontSize: '0.8rem',
      margin: '2px',
      display: 'inline-block',
      border: '1px solid #dee2e6'
    },
    certificateBox: {
      backgroundColor: '#e9f7ef',
      padding: '1rem',
      borderRadius: '8px',
      textAlign: 'center',
      marginBottom: '1rem'
    },
    eligibilityBox: {
      backgroundColor: '#fff3cd',
      padding: '0.8rem',
      borderRadius: '8px',
      textAlign: 'center',
      border: '1px solid #ffeaa7'
    },
    enrollmentSection: {
      backgroundColor: 'white',
      padding: '3rem',
      borderRadius: '15px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      textAlign: 'center',
      marginTop: '3rem'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem'
    },
    statItem: {
      padding: '1rem'
    },
    enrollBtn: {
      backgroundColor: '#007bff',
      color: 'white',
      padding: '1rem 2rem',
      borderRadius: '25px',
      border: 'none',
      fontSize: '1.1rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    modal: {
      display: showEnrollModal ? 'flex' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: '15px',
      maxWidth: '900px',
      width: '95%',
      maxHeight: '95vh',
      overflow: 'auto'
    },
    modalHeader: {
      backgroundColor: '#007bff',
      color: 'white',
      padding: '1.5rem',
      borderRadius: '15px 15px 0 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    modalBody: {
      padding: '2rem'
    },
    sectionTitle: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
      color: '#007bff',
      marginBottom: '1rem',
      borderBottom: '2px solid #007bff',
      paddingBottom: '0.5rem'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    formGroup: {
      marginBottom: '1rem'
    },
    label: {
      display: 'block',
      fontWeight: 'bold',
      marginBottom: '0.5rem',
      color: '#495057'
    },
    input: {
      width: '100%',
      padding: '0.75rem',
      borderRadius: '8px',
      border: '1px solid #ced4da',
      fontSize: '1rem',
      transition: 'border-color 0.3s ease',
      boxSizing: 'border-box'
    },
    textarea: {
      width: '100%',
      padding: '0.75rem',
      borderRadius: '8px',
      border: '1px solid #ced4da',
      fontSize: '1rem',
      minHeight: '80px',
      resize: 'vertical',
      boxSizing: 'border-box'
    },
    checkboxContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      marginTop: '0.5rem'
    },
    checkboxItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    submitBtn: {
      backgroundColor: '#007bff',
      color: 'white',
      padding: '1rem 2rem',
      borderRadius: '25px',
      border: 'none',
      fontSize: '1.1rem',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'background-color 0.3s ease',
      disabled: isSubmitting
    },
    closeBtn: {
      backgroundColor: 'transparent',
      color: 'white',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer'
    },
    note: {
      backgroundColor: '#e9ecef',
      padding: '1rem',
      borderRadius: '8px',
      marginBottom: '1rem',
      fontSize: '0.9rem',
      color: '#6c757d'
    },
    required: {
      color: '#dc3545'
    },
    setupInstructions: {
      backgroundColor: '#d4edda',
      border: '1px solid #c3e6cb',
      borderRadius: '8px',
      padding: '1rem',
      marginBottom: '2rem',
      color: '#155724'
    }
  };


  return (
    <div style={styles.container}>
      <div style={styles.innerContainer}>
        {/* Header Section */}
        <div style={styles.header}>
          <h1 style={styles.title}>
            International Workshops & Conferences
          </h1>
          <p style={styles.subtitle}>
            Join our globally recognized programs featuring real-time company projects, 
            cutting-edge AI research, and international collaboration opportunities.
          </p>
          <div style={styles.highlightBox}>
            <h5 style={{ color: '#28a745', fontWeight: 'bold', marginBottom: '1rem' }}>
              🎯 Program Highlights
            </h5>
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              Real-time Company Projects • International Certification • 
              Job Placement Assistance • Global Networking • Industry Mentorship
            </p>
          </div>
          
          <div style={styles.eligibilityAlert}>
            <h6 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
              🎓 Eligibility Information
            </h6>
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
              Our programs are designed for selected students from recognized institutions. 
              We provide transportation and accommodation for eligible candidates to attend 
              conferences and workshops at prestigious universities and institutions.
            </p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div style={styles.tabContainer}>
          <div 
            style={{
              ...styles.tab,
              ...(activeTab === 'conference' ? styles.activeTab : styles.inactiveTab)
            }}
            onClick={() => setActiveTab('conference')}
          >
            🎓 International Conferences
          </div>
          <div 
            style={{
              ...styles.tab,
              ...(activeTab === 'workshop' ? styles.activeTab : styles.inactiveTab)
            }}
            onClick={() => setActiveTab('workshop')}
          >
            🛠️ Professional Workshops
          </div>
        </div>

        {/* Conference Section */}
        {activeTab === 'conference' && (
          <div>
            <div style={styles.sectionHeader}>
              <h3 style={{ fontWeight: 'bold', marginBottom: '1rem' }}>
                🌍 International Conference Program
              </h3>
              <p style={{ marginBottom: '1rem' }}>
                Present your research at prestigious international conferences held at renowned universities 
                across India and globally. Our academy provides complete support including transportation, 
                accommodation, and guidance for selected students.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                <div>
                  <h6 style={{ marginBottom: '0.5rem' }}>📋 Conference Benefits:</h6>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                    <li>International conference certificate</li>
                    <li>All expenses covered for eligible students</li>
                    <li>Real-time company project presentation</li>
                    <li>Global networking opportunities</li>
                    <li>Research publication support</li>
                  </ul>
                </div>
                <div>
                  <h6 style={{ marginBottom: '0.5rem' }}>🎯 Focus Areas:</h6>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                    <li>AI in Energy & Renewable Energy</li>
                    <li>Large Language Models (LLM)</li>
                    <li>Quantum Computing Applications</li>
                    <li>Industry 4.0 & Smart Technologies</li>
                  </ul>
                </div>
              </div>
            </div>

            <div style={styles.grid}>
              {conferences.map((conference, index) => (
                <div 
                  key={index}
                  style={styles.card}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.1)';
                  }}
                >
                  <div style={styles.cardHeader}>
                    <h5 style={styles.cardTitle}>{conference.title}</h5>
                    {getStatusBadge(conference.status)}
                  </div>
                  <div style={styles.cardBody}>
                    <div style={styles.locationDate}>
                      <p><strong>📍 Location:</strong> {conference.location}</p>
                      <p><strong>📅 Date:</strong> {conference.date}</p>
                    </div>
                    
                    <p style={styles.description}>{conference.description}</p>
                    
                    <div style={styles.topicsContainer}>
                      <h6 style={{ fontWeight: 'bold', color: '#007bff', marginBottom: '0.5rem' }}>
                        Key Topics:
                      </h6>
                      <div>
                        {conference.topics.map((topic, idx) => (
                          <span key={idx} style={styles.topicBadge}>
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div style={styles.certificateBox}>
                      <p style={{ margin: 0, color: '#28a745', fontWeight: 'bold' }}>
                        🏆 {conference.certificate}
                      </p>
                    </div>
                    
                    <div style={styles.eligibilityBox}>
                      <p style={{ margin: 0, color: '#856404', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        📋 Eligibility: {conference.eligibility}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Workshop Section */}
        {activeTab === 'workshop' && (
          <div>
            <div style={{...styles.sectionHeader, ...styles.workshopHeader}}>
              <h3 style={{ fontWeight: 'bold', marginBottom: '1rem' }}>
                🛠️ Professional Workshop Program
              </h3>
              <p style={{ marginBottom: '1rem' }}>
                Participate in our hybrid model workshops conducted across prestigious universities and IITs in India. 
                Our academy provides complete support including transportation to universities and institutions 
                for hands-on learning experiences.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                <div>
                  <h6 style={{ marginBottom: '0.5rem' }}>🎓 Workshop Benefits:</h6>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                    <li>Workshop completion certificate</li>
                    <li>Transportation to universities provided</li>
                    <li>Real-time company project experience</li>
                    <li>Industry collaboration opportunities</li>
                    <li>Job placement assistance</li>
                  </ul>
                </div>
                <div>
                  <h6 style={{ marginBottom: '0.5rem' }}>🌐 Delivery Modes:</h6>
                  <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                    <li>Hybrid workshops at IITs & Universities</li>
                    <li>Virtual international workshops</li>
                    <li>On-campus intensive programs</li>
                    <li>Industry collaboration sessions</li>
                  </ul>
                </div>
              </div>
            </div>

            <div style={styles.grid}>
              {workshops.map((workshop, index) => (
                <div 
                  key={index}
                  style={styles.card}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.1)';
                  }}
                >
                  <div style={{...styles.cardHeader, ...styles.workshopCardHeader}}>
                    <h5 style={styles.cardTitle}>{workshop.title}</h5>
                  </div>
                  <div style={styles.cardBody}>
                    <div style={styles.locationDate}>
                      <p><strong>📍 Location:</strong> {workshop.location}</p>
                      <p><strong>🎯 Mode:</strong> 
                        <span style={{
                          backgroundColor: '#17a2b8',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontSize: '0.8rem',
                          marginLeft: '0.5rem'
                        }}>
                          {workshop.mode}
                        </span>
                      </p>
                    </div>
                    
                    <p style={styles.description}>{workshop.description}</p>
                    
                    <div style={styles.topicsContainer}>
                      <h6 style={{ fontWeight: 'bold', color: '#28a745', marginBottom: '0.5rem' }}>
                        Workshop Features:
                      </h6>
                      <div>
                        {workshop.features.map((feature, idx) => (
                          <span key={idx} style={styles.topicBadge}>
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div style={styles.certificateBox}>
                      <p style={{ margin: 0, color: '#28a745', fontWeight: 'bold' }}>
                        🏆 {workshop.certificate}
                      </p>
                    </div>
                    
                    <div style={styles.eligibilityBox}>
                      <p style={{ margin: 0, color: '#856404', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        📋 Eligibility: {workshop.eligibility}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Enrollment Section */}
        <div style={styles.enrollmentSection}>
          <h3 style={{ color: '#007bff', fontWeight: 'bold', marginBottom: '1rem' }}>
            🚀 Ready to Join Our International Programs?
          </h3>
          <p style={{ color: '#6c757d', marginBottom: '2rem', fontSize: '1.1rem' }}>
            Take the first step towards your global career. Our expert team will review your profile 
            and guide you through the selection process for our prestigious international programs.
          </p>
          
        <div style={styles.statsGrid}>
            <div style={styles.statItem}>
              <h4 style={{ color: '#007bff', fontWeight: 'bold', margin: 0 }}>500+</h4>
              <p style={{ color: '#6c757d', margin: 0 }}>Students Placed</p>
            </div>
            <div style={styles.statItem}>
              <h4 style={{ color: '#28a745', fontWeight: 'bold', margin: 0 }}>50+</h4>
              <p style={{ color: '#6c757d', margin: 0 }}>Partner Companies</p>
            </div>
            <div style={styles.statItem}>
              <h4 style={{ color: '#dc3545', fontWeight: 'bold', margin: 0 }}>95%</h4>
              <p style={{ color: '#6c757d', margin: 0 }}>Success Rate</p>
            </div>
            <div style={styles.statItem}>
              <h4 style={{ color: '#ffc107', fontWeight: 'bold', margin: 0 }}>10+</h4>
              <p style={{ color: '#6c757d', margin: 0 }}>Countries</p>
            </div>
          </div>
          
          <button 
            style={styles.enrollBtn}
            onClick={() => setShowEnrollModal(true)}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#0056b3';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#007bff';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            🎓 Apply Now - Free Enrollment
          </button>
          
          <p style={{ color: '#6c757d', marginTop: '1rem', fontSize: '0.9rem' }}>
            * No enrollment fees • Complete support provided for eligible students
          </p>
        </div>

        {/* Enrollment Modal */}
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h4 style={{ margin: 0, fontWeight: 'bold' }}>
                🎓 International Program Enrollment
              </h4>
              <button 
                style={styles.closeBtn}
                onClick={() => setShowEnrollModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={styles.note}>
                <strong>📝 Important:</strong> Please fill out this comprehensive form to help us evaluate your 
                eligibility for our international programs. We provide complete support including transportation 
                and accommodation for selected candidates.
              </div>
              
              <form onSubmit={handleSubmit}>
                {/* Personal Information */}
                <div style={styles.sectionTitle}>
                  👤 Personal Information
                </div>
                
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Full Name <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter your full name"
                      required
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Mobile Number <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter mobile number"
                      required
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Email Address <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Gender <span style={styles.required}>*</span>
                    </label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      style={styles.input}
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Date of Birth</label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      style={styles.input}
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Father's Name</label>
                    <input
                      type="text"
                      name="fatherName"
                      value={formData.fatherName}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter father's name"
                    />
                  </div>
                </div>
                
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Mother's Name</label>
                    <input
                      type="text"
                      name="motherName"
                      value={formData.motherName}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter mother's name"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter city"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter state"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Pincode</label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter pincode"
                    />
                  </div>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Address</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    style={styles.textarea}
                    placeholder="Enter complete address"
                  />
                </div>

                {/* Educational Information */}
                <div style={styles.sectionTitle}>
                  🎓 Educational Information
                </div>
                
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      College/Institution <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="college"
                      value={formData.college}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter college name"
                      required
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>University</label>
                    <input
                      type="text"
                      name="university"
                      value={formData.university}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter university name"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Department <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Enter department"
                      required
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Educational Background <span style={styles.required}>*</span>
                    </label>
                    <select
                      name="educationalBackground"
                      value={formData.educationalBackground}
                      onChange={handleInputChange}
                      style={styles.input}
                      required
                    >
                      <option value="">Select Background</option>
                      <option value="Undergraduate">Undergraduate</option>
                      <option value="Postgraduate">Postgraduate</option>
                      <option value="PhD">PhD</option>
                      <option value="Working Professional">Working Professional</option>
                    </select>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Degree</label>
                    <input
                      type="text"
                      name="degree"
                      value={formData.degree}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="B.Tech, M.Tech, etc."
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Specialization</label>
                    <input
                      type="text"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Computer Science, Electronics, etc."
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>
                      Passing Year <span style={styles.required}>*</span>
                    </label>
                    <input
                      type="number"
                      name="passingYear"
                      value={formData.passingYear}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="2024"
                      min="2015"
                      max="2030"
                      required
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Current Semester</label>
                    <input
                      type="text"
                      name="currentSemester"
                      value={formData.currentSemester}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="1st, 2nd, etc."
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>CGPA</label>
                    <input
                      type="text"
                      name="cgpa"
                      value={formData.cgpa}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="8.5"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Percentage</label>
                    <input
                      type="text"
                      name="percentage"
                      value={formData.percentage}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="85%"
                    />
                  </div>
                </div>

                {/* Professional Information */}
                <div style={styles.sectionTitle}>
                  💼 Professional Information
                </div>
                
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Are you a working professional?</label>
                    <select
                      name="workingProfessional"
                      value={formData.workingProfessional}
                      onChange={handleInputChange}
                      style={styles.input}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  
                  {formData.workingProfessional === 'Yes' && (
                    <>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Company Name</label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="Enter company name"
                        />
                      </div>
                      
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Designation</label>
                        <input
                          type="text"
                          name="designation"
                          value={formData.designation}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="Enter designation"
                        />
                      </div>
                      
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Work Experience (Years)</label>
                        <input
                          type="text"
                          name="workExperience"
                          value={formData.workExperience}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="2.5"
                        />
                      </div>
                      
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Current Salary (LPA)</label>
                        <input
                          type="text"
                          name="currentSalary"
                          value={formData.currentSalary}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="5.5"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Career Information */}
                <div style={styles.sectionTitle}>
                  🎯 Career Information
                </div>
                
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Any career gap?</label>
                    <select
                      name="careerGap"
                      value={formData.careerGap}
                      onChange={handleInputChange}
                      style={styles.input}
                    >
                      <option value="">Select</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                  
                  {formData.careerGap === 'Yes' && (
                    <>
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Gap Reason</label>
                        <input
                          type="text"
                          name="gapReason"
                          value={formData.gapReason}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="Higher studies, personal reasons, etc."
                        />
                      </div>
                      
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Gap Duration</label>
                        <input
                          type="text"
                          name="gapDuration"
                          value={formData.gapDuration}
                          onChange={handleInputChange}
                          style={styles.input}
                          placeholder="6 months, 1 year, etc."
                        />
                      </div>
                    </>
                  )}
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Expected Salary (LPA)</label>
                    <input
                      type="text"
                      name="expectedSalary"
                      value={formData.expectedSalary}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="8.0"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Preferred Location</label>
                    <input
                      type="text"
                      name="preferredLocation"
                      value={formData.preferredLocation}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Bangalore, Mumbai, etc."
                    />
                  </div>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Career Goals</label>
                  <textarea
                    name="careerGoals"
                    value={formData.careerGoals}
                    onChange={handleInputChange}
                    style={styles.textarea}
                    placeholder="Describe your short-term and long-term career goals"
                  />
                </div>

                {/* Program Information */}
                <div style={styles.sectionTitle}>
                  📚 Program Information
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Program Interest <span style={styles.required}>*</span>
                  </label>
                  <div style={styles.checkboxContainer}>
                    {[
                      'International Conferences',
                      'Professional Workshops',
                      'AI & Machine Learning',
                      'Quantum Computing',
                      'Energy Sector Applications',
                      'Industry Collaboration',
                      'Research & Development'
                    ].map((interest) => (
                      <div key={interest} style={styles.checkboxItem}>
                        <input
                          type="checkbox"
                          name="interest"
                          value={interest}
                          checked={formData.interest.includes(interest)}
                          onChange={handleInputChange}
                        />
                        <label>{interest}</label>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Preferred Mode</label>
                    <select
                      name="preferredMode"
                      value={formData.preferredMode}
                      onChange={handleInputChange}
                      style={styles.input}
                    >
                      <option value="">Select Mode</option>
                      <option value="On-campus">On-campus</option>
                      <option value="Virtual">Virtual</option>
                      <option value="Hybrid">Hybrid</option>
                      <option value="No preference">No preference</option>
                    </select>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Previous Workshops Attended</label>
                    <input
                      type="text"
                      name="previousWorkshops"
                      value={formData.previousWorkshops}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="List any relevant workshops"
                    />
                  </div>
                </div>
                
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Technical Skills</label>
                    <textarea
                      name="technicalSkills"
                      value={formData.technicalSkills}
                      onChange={handleInputChange}
                      style={styles.textarea}
                      placeholder="List your technical skills (Python, Java, Machine Learning, etc.)"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Programming Languages</label>
                    <textarea
                      name="programmingLanguages"
                      value={formData.programmingLanguages}
                      onChange={handleInputChange}
                      style={styles.textarea}
                      placeholder="Python, Java, C++, JavaScript, etc."
                    />
                  </div>
                </div>

                {/* Additional Information */}
                <div style={styles.sectionTitle}>
                  ℹ️ Additional Information
                </div>
                
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Hobbies</label>
                    <input
                      type="text"
                      name="hobbies"
                      value={formData.hobbies}
                      onChange={handleInputChange}
                      style={styles.input}
                      placeholder="Reading, sports, music, etc."
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>How did you hear about us?</label>
                    <select
                      name="hearAboutUs"
                      value={formData.hearAboutUs}
                      onChange={handleInputChange}
                      style={styles.input}
                    >
                      <option value="">Select source</option>
                      <option value="Social Media">Social Media</option>
                      <option value="College">College</option>
                      <option value="Friends">Friends</option>
                      <option value="Website">Website</option>
                      <option value="Advertisement">Advertisement</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Achievements</label>
                    <textarea
                      name="achievements"
                      value={formData.achievements}
                      onChange={handleInputChange}
                      style={styles.textarea}
                      placeholder="List your notable achievements, awards, certifications"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>References</label>
                    <textarea
                      name="references"
                      value={formData.references}
                      onChange={handleInputChange}
                      style={styles.textarea}
                      placeholder="Faculty, employer, or professional references"
                    />
                  </div>
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Additional Information</label>
                  <textarea
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleInputChange}
                    style={styles.textarea}
                    placeholder="Any additional information you'd like to share"
                  />
                </div>

                {/* Agreement */}
                <div style={styles.sectionTitle}>
                  ✅ Agreement
                </div>
                
                <div style={styles.checkboxContainer}>
                  <div style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      name="agreeToTerms"
                      checked={formData.agreeToTerms}
                      onChange={handleInputChange}
                      required
                    />
                    <label>
                      <span style={styles.required}>*</span> I agree to the terms and conditions and 
                      understand that my application will be reviewed for eligibility.
                    </label>
                  </div>
                  
                  <div style={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      name="agreeToWhatsApp"
                      checked={formData.agreeToWhatsApp}
                      onChange={handleInputChange}
                    />
                    <label>
                      I agree to receive updates and communications via WhatsApp and email.
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                  <button
                    type="submit"
                    style={{
                      ...styles.submitBtn,
                      opacity: isSubmitting ? 0.6 : 1,
                      cursor: isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? '🔄 Submitting...' : '🚀 Submit Application'}
                  </button>
                  
                  <p style={{ color: '#6c757d', marginTop: '1rem', fontSize: '0.9rem' }}>
                    We will review your application and contact you within 24-48 hours.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkshopConference;